import { Button, Grid, Typography } from "@mui/material";
import { formatDateRangeForAPICall } from "@shared/charts/formatDate";
import { DateRange } from "@shared/components/DateRangePicker";
import { useRemergeSwrData } from "@shared/http/RemergeApiSwr";
import { SHERLOCK_CHANGES, SherlockChangeType, SherlockMetricType } from "@shared/http/apiTypes/sherlockApiTypes";
import { data } from "@shared/http/jsonApi";
import { RemergeAPIContext } from "@shared/http/remergeApiContext";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSherlockTracking } from "../hooks/useSherlockTracking";
import InvestigationResults from "./InvestigationResult";
import InvestigationSettings from "./InvestigationSettings";
import classes from "./SherlockDashboard.module.css";
import useSherlockSettingsValues from "./chart/useSherlockSettingsValues";

function SherlockDashboard({ userId, logOut }: { userId: string; logOut: () => void }) {
  const {
    trackDashboardViewed,
    trackDateRangeSelected,
    trackCampaignSelected,
    trackMetricSelected,
    trackChangeFilterApplied,
  } = useSherlockTracking(userId);

  const { dateRange, onDateRangeChange, campaignIdFromUrl, metricFromUrl, changesFromUrl } =
    useSherlockSettingsValues();

  const allChanges = Object.keys(SHERLOCK_CHANGES);
  const changes = changesFromUrl ? changesFromUrl.split(",") : allChanges;

  const [selectedOrganization, setSelectedOrganization] = useState<string>("");
  const [selectedCampaign, setSelectedCampaign] = useState<string | null>(campaignIdFromUrl);
  const [selectedChanges, setSelectedChanges] = useState<SherlockChangeType[]>(changes as SherlockChangeType[]);
  const [selectedMetric, setSelectedMetric] = useState<SherlockMetricType>(
    (metricFromUrl as SherlockMetricType) || "ap_conversions",
  );
  const [resultsError, setResultsError] = useState<string | null>(null);
  const [currency, setCurrency] = useState<string>("USD");

  const { startDate, endDate } = useMemo(() => formatDateRangeForAPICall(dateRange), [dateRange]);

  const queryParams = useMemo(() => new URLSearchParams(window.location.search), []);

  const remergeApi = useContext(RemergeAPIContext);

  useEffect(() => {
    const fetchCampaignOrg = async () => {
      if (campaignIdFromUrl) {
        const campaign = await data(remergeApi.campaignsShow(campaignIdFromUrl));
        const orgId = campaign.attributes.organization_id;
        setSelectedOrganization(orgId.toString());
      }
    };
    fetchCampaignOrg();
  }, [campaignIdFromUrl, remergeApi]);

  useEffect(() => {
    trackDashboardViewed();
  }, [trackDashboardViewed]);

  const { data: organizations } = useRemergeSwrData(remergeApi.organizationsIndexRequest());

  function organizationOptions() {
    if (!organizations) return [];
    return organizations.map(org => ({
      value: org.id,
      label: `[${org.id}] ${org.attributes.name}`,
    }));
  }

  const { data: campaigns } = useRemergeSwrData(
    remergeApi.campaignsIndexRequest({ organizationId: selectedOrganization }),
  );

  function campaignOptions() {
    if (!campaigns) return [];
    return campaigns.map(campaign => ({
      value: campaign.id,
      label: `[${campaign.id}] ${campaign.attributes.name}`,
    }));
  }

  const onOrganizationSelect = useCallback((org: string) => {
    setSelectedOrganization(org);
    setResultsError(null);
  }, []);

  const setQueryParams = useCallback(() => {
    history.replaceState(null, "", "?" + queryParams.toString());
  }, [queryParams]);

  const onCampaignChange = useCallback(
    async (selectedCampaign: string) => {
      queryParams.set("campaignId", selectedCampaign);
      setQueryParams();

      const campaign = await data(remergeApi.campaignsShow(selectedCampaign));
      const currencyId = campaign.attributes.currency_id || "USD";
      const currencies = await data(remergeApi.currenciesIndex());
      const currency = currencies.find((c: { id: string }) => c.id === currencyId)?.attributes.symbol || "$";
      setCurrency(currency);

      setSelectedCampaign(selectedCampaign);
      setResultsError(null);

      trackCampaignSelected(selectedOrganization, selectedCampaign);
    },
    [queryParams, setQueryParams, remergeApi, trackCampaignSelected, selectedOrganization],
  );

  const onMetricChange = useCallback(
    (selectedMetric: SherlockMetricType) => {
      queryParams.set("metric", selectedMetric);
      setQueryParams();

      setSelectedMetric(selectedMetric);
      setResultsError(null);

      trackMetricSelected(selectedMetric);
    },
    [queryParams, setQueryParams, trackMetricSelected],
  );

  const onChangesToDisplayChange = useCallback(
    (selectedChange: SherlockChangeType) => {
      setSelectedChanges((prevState: SherlockChangeType[]) => {
        const updatedChanges = prevState.includes(selectedChange)
          ? prevState.filter(change => change !== selectedChange)
          : [...prevState, selectedChange];

        queryParams.set("changes", updatedChanges.join(","));
        setQueryParams();

        const filterState = updatedChanges.includes(selectedChange);
        trackChangeFilterApplied(selectedChange, filterState);

        return updatedChanges;
      });
      setResultsError(null);
    },
    [queryParams, setQueryParams, trackChangeFilterApplied],
  );

  const onDateRangeChangeWrapper = useCallback(
    (range: DateRange) => {
      onDateRangeChange(range);
      setResultsError(null);

      trackDateRangeSelected(range.startDate as Date, range.endDate as Date);
    },
    [onDateRangeChange, trackDateRangeSelected],
  );

  return (
    <>
      <Grid container className={classes.dashboard} direction="column" spacing={2} alignItems="center">
        <Grid item marginTop={4}>
          <Typography align="center" className={classes.mainHeader}>
            Sherlock Insights
          </Typography>
        </Grid>

        <Grid item>
          <Button className={classes.logoutButton} onClick={logOut} variant="outlined" size="small">
            Log Out
          </Button>
        </Grid>

        <Grid item>
          <InvestigationSettings
            campaignOptions={campaignOptions()}
            organizationOptions={organizationOptions()}
            dateRange={dateRange}
            setDateRange={onDateRangeChangeWrapper}
            selectedOrganization={selectedOrganization}
            onOrganizationSelect={onOrganizationSelect}
            selectedCampaign={selectedCampaign as string}
            onCampaignChange={onCampaignChange}
            selectedChanges={selectedChanges}
            onChangesToDisplayChange={onChangesToDisplayChange}
            selectedMetric={selectedMetric}
            onMetricChange={onMetricChange}
          />
        </Grid>

        <Grid item sx={{ width: 1200 }} textAlign={"center"} marginBottom={4}>
          {selectedCampaign ? (
            <InvestigationResults
              selectedChanges={selectedChanges}
              selectedOrganization={selectedOrganization}
              currencySymbol={currency}
              selectedCampaign={selectedCampaign}
              selectedMetric={selectedMetric}
              startDate={startDate as string}
              endDate={endDate as string}
              resultsError={resultsError}
              setResultsError={setResultsError}
              userId={userId}
            />
          ) : (
            <Grid marginTop={10}>
              <Typography className={classes.selectCampaignHint}>Select the campaign to generate the chart</Typography>
            </Grid>
          )}
        </Grid>
      </Grid>
    </>
  );
}

export default SherlockDashboard;
